import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, KeyRound, CheckCircle2, Mail } from "lucide-react";
import { BrandLogo } from "@/projects/components/brand-logo";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Card } from "@/projects/components/ui/card";
import { toast } from "sonner";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Forgot Password — રાજનગર જ્ઞાનોત્સવ 2.0" },
      { name: "description", content: "Reset your Rajnagar Gyanotsav 2.0 account password." },
    ],
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "teacher">("teacher");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email or username");
      return;
    }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 600));
    setSent(true);
    setLoading(false);
    toast.success("Reset instructions sent (demo).");
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background p-4">
      <div className="absolute inset-0 gradient-hero opacity-95" />
      <div className="absolute inset-0 motif-bg opacity-50" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="mb-4 inline-flex items-center gap-2 text-sm text-white/90 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <Card className="p-8 shadow-card">
          <div className="mb-6 flex items-center gap-3">
            <BrandLogo className="h-11 w-11" />
            <div>
              <div className="text-guj text-sm font-bold">રાજનગર જ્ઞાનોત્સવ 2.0</div>
              <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">Account Recovery</div>
            </div>
          </div>

          {!sent ? (
            <>
              <div className="inline-flex items-center gap-2 rounded-full border border-saffron/40 bg-saffron-soft px-3 py-1 text-xs font-medium text-saffron">
                <KeyRound className="h-3.5 w-3.5" /> Forgot Password
              </div>
              <h1 className="mt-3 text-2xl font-bold">Reset your password</h1>
              <p className="mt-1 text-sm text-muted-foreground">Enter your account details and we'll send you a reset link.</p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <Label>Account Type</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    {(["teacher", "admin"] as const).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRole(r)}
                        className={`rounded-md border px-3 py-2 text-sm font-medium capitalize transition-colors ${role === r ? "border-royal bg-royal-soft text-royal" : "hover:bg-muted"}`}
                      >
                        {r === "admin" ? "Super Admin" : "Teacher"}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email or Username</Label>
                  <Input
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={role === "admin" ? "admin@rajnagar.edu" : "your.email@school.edu"}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>

              <div className="mt-6 flex items-center justify-between text-xs text-muted-foreground">
                <Link to="/login/teacher" className="hover:text-foreground">← Teacher login</Link>
                <Link to="/login/admin" className="hover:text-foreground">Admin login →</Link>
              </div>
            </>
          ) : (
            <div className="py-6 text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success/10 text-success">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h1 className="mt-4 text-xl font-bold">Check your inbox</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent password reset instructions to it.
              </p>
              <div className="mt-5 rounded-lg border bg-muted/40 p-3 text-left text-xs">
                <div className="flex items-center gap-2 font-medium text-foreground">
                  <Mail className="h-3.5 w-3.5" /> Demo mode
                </div>
                <div className="mt-1 text-muted-foreground">
                  No email is actually sent. Use the demo credentials shown on the login page.
                </div>
              </div>
              <div className="mt-6 flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setSent(false); setEmail(""); }}>
                  Try another email
                </Button>
                <Button className="flex-1" onClick={() => navigate({ to: role === "admin" ? "/login/admin" : "/login/teacher" })}>
                  Go to login
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
